import React, { useEffect, useState } from 'react';
import {
  IconButton,
  LoadingSpinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from '@qualtrics/ui-react';
import { ChevronDownIcon, ChevronRightIcon } from '@qualtrics/ui-react/icons';
import styles from './nonEvaluationExperienceStudentPortal.module.css';
import ColoredStatus from '../ColoredStatus/coloredStatus';
import {
  getEvaluationPage,
  getEvaluationTasks,
  getSurveyLinkUrl,
  isRetakeLinksEnabled,
} from '../services/evaluationPageService';
import { getStudentByHashedStudentId } from '../services/studentService';
import OptOutLink from '../OptOutLink/optOutLink';
import Cookies from 'js-cookie';
import RetakeLinksStatus from '../RetakeLinksStatus/retakeLinksStatus';
import StudentInfo from '../StudentInfo/studentInfo';

const EXPAND_ALL_ROWS_COOKIE_NAME = 'expandAllRows';
const brandId = document.getElementById('bId').textContent;
const hashedStudentId = document.getElementById('uId').textContent;
const language = document.getElementById('language').textContent;
const channel = document.getElementById('channel').textContent;

const DEFAULT_BROWSER_TITLE = "Qualtrics Course Evaluations - Student Portal";

const getTeachersInTaskGroup = (tasks) => {
  const teachers = [];
  tasks
    .filter((task) => task.teacherId !== null)
    .forEach((task) => {
      const teacherName = task.teacherName;
      if (!teachers.includes(teacherName)) {
        teachers.push(teacherName);
      }
    });
  return teachers;
};
const getCourseCellText = (taskGroup) => {
  const { courseCode, courseName, courseId } = taskGroup[0];

  // TODO: Remove brandsToOnlyShowCourseName once custom course display functionality is added
  const brandsToOnlyShowCourseName = ['qutc', 'kusurvey'];
  if (courseCode && courseName && !brandsToOnlyShowCourseName.includes(brandId)) {
    return `${courseCode}: ${courseName}`;
  }

  return courseName || courseCode || courseId;
};
const getInitialExpandRowsValueFromCookies = () => {
  const expandAllRows = Cookies.get(EXPAND_ALL_ROWS_COOKIE_NAME);
  if (expandAllRows) {
    // Parse string value to boolean
    return JSON.parse(expandAllRows);
  }
  return false;
};

const NonEvaluationExperienceStudentPortal = () => {
  const [evaluationPage, setEvaluationPage] = useState({});
  const [evaluationTasks, setEvaluationTasks] = useState([]);
  const [areRetakesEnabled, setAreRetakesEnabled] = useState(undefined);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [studentId, setStudentId] = useState('');

  const [expandRows, setExpandRows] = useState([]);
  const [expandAllRows, setExpandAllRows] = useState(getInitialExpandRowsValueFromCookies());

  useEffect(() => {
    const getEvaluationData = async () => {
      const evaluationPagePromise = getEvaluationPage(
        brandId,
        hashedStudentId,
        language
      );
      const evaluationTasksPromise = getEvaluationTasks(
        brandId,
        hashedStudentId,
        language
      );
      const retakesEnabledPromise = isRetakeLinksEnabled(brandId);
      const studentPromise = getStudentByHashedStudentId(hashedStudentId, brandId);
      const [evaluationPage, evaluationTasks, retakeLinksEnabled, student] = await Promise.all([
        evaluationPagePromise,
        evaluationTasksPromise,
        retakesEnabledPromise,
        studentPromise
      ]);
      setEvaluationPage(evaluationPage);
      setEvaluationTasks(evaluationTasks);
      setAreRetakesEnabled(retakeLinksEnabled);
      setStudentId(student["userId"]);
      setIsPageLoading(false);
      setExpandRows(evaluationTasks.map(_ => expandAllRows));
      document.title = evaluationPage.browserTitleText !== null
                ? evaluationPage.browserTitleText
                : DEFAULT_BROWSER_TITLE;
    };
    getEvaluationData();
  }, []);

  const surveyLinkOnClick = async (task) => {
    try {
      setIsPageLoading(true);
      const surveyUrl = await getSurveyLinkUrl(task.transactionId, task.distributionId, brandId, language, channel, hashedStudentId);
      window.location = surveyUrl;
    } catch (e) {
      setIsPageLoading(false);
    }
  }

  const SurveyLinks = ({taskGroup}) => {
    return taskGroup.map((task) => {
      let surveyLinkStyle = styles['survey-link'];
      let surveyLinkHoverText = undefined;
      let isDisabledLink = false;


      if (task.completed) {
        surveyLinkStyle += ' ' + styles['completed-link'];

        if (areRetakesEnabled) {
          surveyLinkStyle += ' ' + styles['retake-link'];
        } else {
          surveyLinkStyle += ' ' + styles['disabled-link'];
          isDisabledLink = true;
        }
      }

      const surveyLinkText = task.evalType === 'COURSE' ?
          evaluationPage.courseEvaluationLinkText :  
          `${evaluationPage.teacherEvaluationLinkText}: ${task.teacherName}`;

      return (
        <div className={styles['cell-text-margin']}>
          <Tooltip content={surveyLinkHoverText} delayMs={500} placement="left" disabled={surveyLinkHoverText === undefined}>
            <div className={surveyLinkStyle} onClick={isDisabledLink ? undefined : () => surveyLinkOnClick(task)}>
              {surveyLinkText}
            </div>
          </Tooltip>
        </div>
      );
    });
  }

  return (
    <div className={styles['container']}>
    {isPageLoading ? <LoadingSpinner show size="large" className={styles['spinner']}/> :
        <>
        <div className={styles['top-row-wrapper']}>
          {evaluationPage.logoUrl && (
              <div className={styles['logo']}>
              <img src={evaluationPage.logoUrl} className={styles['logo-image']}></img>
              </div>
          )}
          <StudentInfo hashedStudentId={hashedStudentId} brandId={brandId} />
        </div>
        <div className={styles['header']}>{evaluationPage.header}</div>
        <div className={styles['description']}>{evaluationPage.description}</div>
        <Table>
            <TableHead>
            <TableRow>
                <TableHeaderCell className={`${styles['table-header']} ${styles['expand-header']}`}>
                {
                    <IconButton onClick={() => {
                    const newExpandAllRows = !expandAllRows;
                    setExpandRows(expandRows.map(_ => newExpandAllRows));
                    setExpandAllRows(newExpandAllRows);
                    
                    // sameSite: None and secure: true are required for cookies to work when the student portal
                    // is embedded in an iframe in an LMS. Detailed explanation: https://medium.com/trabe/cookies-and-iframes-f7cca58b3b9e
                    Cookies.set(EXPAND_ALL_ROWS_COOKIE_NAME, newExpandAllRows, {sameSite: 'None', secure: true});
                    }}>
                    {expandAllRows ? <ChevronDownIcon  /> : <ChevronRightIcon  />}
                    </IconButton>
                }
                </TableHeaderCell>
                <TableHeaderCell className={`${styles['table-header']} ${styles['status-header']}`}>
                {evaluationPage.statusTitle}
                </TableHeaderCell>
                <TableHeaderCell className={styles['table-header']}>
                {evaluationPage.evaluationTitle}
                </TableHeaderCell>
                <TableHeaderCell className={styles['table-header']}>
                {evaluationPage.courseTitle}
                </TableHeaderCell>
                <TableHeaderCell className={styles['table-header']}>
                {evaluationPage.teacherTitle}
                </TableHeaderCell>
            </TableRow>
            </TableHead>
            <TableBody>
            {evaluationTasks.map((taskGroup, i) => {
                const teachersInTaskGroup = getTeachersInTaskGroup(taskGroup);
                const allEvalsComplete = taskGroup.reduce((allComplete, task) => {
                return allComplete && task.completed;
                }, taskGroup[0].completed);
                if (!expandRows[i]) {
                taskGroup = taskGroup.slice(0, 1);
                }

                return (
                <TableRow>
                    <TableCell className={styles['expand-cell']}>
                    <IconButton onClick={() => {
                        const newExpandRows = [...expandRows];
                        newExpandRows[i] = !newExpandRows[i];
                        setExpandRows(newExpandRows);
                    }}>
                        {expandRows[i] ? <ChevronDownIcon  /> : <ChevronRightIcon  />}
                    </IconButton>
                    </TableCell>
                    <TableCell>
                    <div>
                        <ColoredStatus
                        status={allEvalsComplete ? 'Done' : 'Todo'}
                        translatedStatus={
                            allEvalsComplete
                            ? evaluationPage.evaluationComplete
                            : evaluationPage.evaluationIncomplete
                        }
                        />
                    </div>
                    </TableCell>
                    <TableCell>
                    <SurveyLinks taskGroup={taskGroup} />
                    </TableCell>
                    <TableCell className={styles['table-cell-text']}>
                    <div>{getCourseCellText(taskGroup)}</div>
                    </TableCell>
                    <TableCell className={styles['table-cell-text']}>
                    {teachersInTaskGroup.map((teacherName) => {
                        return (
                        <div className={styles['cell-text-margin']}>
                            {teacherName}
                        </div>
                        );
                    })}
                    </TableCell>
                </TableRow>
                );
            })}
            </TableBody>
        </Table>
        <div className={styles['footer']}>
            <RetakeLinksStatus retakeLinksEnabled={areRetakesEnabled} />
            <OptOutLink evaluationTasks={evaluationTasks} hashedStudentId={hashedStudentId} brandId={brandId} studentId={studentId} />
        </div>
        </>
    }
    </div>
  );
};

export default NonEvaluationExperienceStudentPortal;
